import React from "react"
import { AxisLeft, AxisBottom } from "@visx/axis"
import type { AxisScale } from "@visx/axis"
import { CHART_COLORS } from "../theme"

type ChartAxisLeftProps = {
    scale: AxisScale
    left?: number
    labelColor?: string
    numTicks?: number
}

type ChartAxisBottomProps = {
    scale: AxisScale
    top?: number
    tickFormat?: (value: Date | { valueOf(): number }, index: number) => string
    numTicks?: number
    tickValues?: Date[]
    labelColor?: string
}

/**
 * Standard left (y) axis used across all charts.
 * Renders without axis line or tick marks; only tick labels.
 *
 * @param scale      - A visx scale (scaleLinear, scaleTime, etc.)
 * @param left       - Horizontal offset in pixels (use margin.left for non-transformed SVG)
 * @param labelColor - Tick label fill color (defaults to CHART_COLORS.label)
 * @param numTicks   - Suggested number of ticks
 */
export const StandardAxisLeft = ({
    scale,
    left,
    labelColor = CHART_COLORS.label,
    numTicks,
}: ChartAxisLeftProps) => (
    <AxisLeft
        left={left}
        scale={scale}
        hideAxisLine
        hideTicks
        numTicks={numTicks}
        tickLabelProps={() => ({
            fill: labelColor,
            fontSize: 11,
            textAnchor: "end" as const,
            dy: "0.33em",
            dx: "-10px",
        })}
    />
)

/**
 * Standard bottom (x) axis used across all charts.
 * Renders without axis line or tick marks; only tick labels.
 *
 * @param scale       - A visx scale (scaleTime, scaleBand, etc.)
 * @param top         - Vertical position in pixels (use innerHeight + margin.top for non-transformed SVG)
 * @param tickFormat  - Formats each tick value into a display string
 * @param numTicks    - Suggested number of ticks
 * @param labelColor  - Tick label fill color (defaults to CHART_COLORS.label)
 */
export const StandardAxisBottom = ({
    scale,
    top,
    tickFormat,
    numTicks,
    tickValues,
    labelColor = CHART_COLORS.label,
}: ChartAxisBottomProps) => (
    <AxisBottom
        top={top}
        scale={scale}
        hideAxisLine
        hideTicks
        tickFormat={tickFormat}
        numTicks={numTicks}
        tickValues={tickValues}
        tickLabelProps={() => ({
            fill: labelColor,
            fontSize: 11,
            textAnchor: "middle" as const,
            dy: "10px",
        })}
    />
)
